import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import bookingService from "../../services/bookingService";
import logger from "../../utils/logger";
import { clearPendingPhotos } from "./photoStore";
import { startUpload, resetUploadState } from "./photoUploadManager";
import { getBookingForm, shouldShowQuestion } from "../../config/bookingForms";
import {
  saveProgressToStorage,
  loadProgressFromStorage,
  clearProgressFromStorage,
} from "../../utils/bookingProgress";

// ==================== HELPER FUNCTIONS ====================

/**
 * Save booking progress to localStorage
 */
function saveProgress(state) {
  try {
    // Probably never called on confirmation step, but just in case
    if (state.currentStep === "confirmation") {
      clearProgressFromStorage();
      return;
    }

    const progress = {
      serviceSlug: state.serviceSlug,
      currentStep: state.currentStep,
      currentQuestionIndex: state.currentQuestionIndex,
      formData: state.formData,
      estimate: state.estimate,
      // Address selection state
      savedAddressId: state.savedAddressId,
      saveAddress: state.saveAddress,
    };
    saveProgressToStorage(progress);
    logger.debug("Booking progress saved to localStorage");
  } catch (error) {
    logger.error("Failed to save booking progress:", error);
  }
}

/**
 * Load booking progress from localStorage
 */
function loadProgress(serviceSlug) {
  const progress = loadProgressFromStorage(serviceSlug);
  if (progress) {
    logger.debug("Loaded booking progress from localStorage");
  }
  return progress;
}

/**
 * Clear booking progress from localStorage
 */
function clearProgress() {
  clearProgressFromStorage();
  logger.debug("Booking progress cleared from localStorage");
}

/**
 * Virtual question appended to every service details flow for photo uploads.
 * Not stored in formData — File objects live in photoStore.
 */
export const PHOTOS_QUESTION = {
  id: "photos",
  type: "photos",
  question: "Have any photos that could help?",
  description:
    "Photos help your service provider understand the job before they arrive.",
  required: false,
};

/**
 * Get visible questions (excluding conditional questions that shouldn't show)
 * Always appends the photos question at the end.
 */
function getVisibleQuestions(questions, answers) {
  const visible = questions.filter((q) => shouldShowQuestion(q, answers));
  return [...visible, PHOTOS_QUESTION];
}

// ==================== ASYNC THUNKS ====================

/**
 * Get price estimate from backend
 */
export const fetchPriceEstimate = createAsyncThunk(
  "bookingFlow/fetchPriceEstimate",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().bookingFlow;

      const estimateData = {
        categoryId: state.categoryId,
        details: state.formData.serviceDetails,
      };

      // Include region from address if available
      const region =
        state.formData.address?.region || state.formData.address?.province;
      if (region) {
        estimateData.region = region;
      }

      // Include requested date/time if available
      if (state.formData.datetime?.dateTime) {
        estimateData.requestedDateTime = state.formData.datetime.dateTime;
      }

      logger.debug("Fetching estimate with data:", estimateData);

      const data = await bookingService.fetchEstimate(estimateData);

      logger.debug("Estimate received:", data);
      return data;
    } catch (error) {
      logger.error("Error in fetchEstimate thunk:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch estimate";
      return rejectWithValue(message);
    }
  },
);

/**
 * Submit booking
 */
export const submitBooking = createAsyncThunk(
  "bookingFlow/submit",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { formData, estimate, categoryId, savedAddressId, saveAddress } =
        state.bookingFlow;

      // Prepare base booking payload
      const bookingPayload = {
        categoryId,
        requestedDateTime: formData.datetime.dateTime,
        details: formData.serviceDetails,
        estimatedPriceRange: [estimate.min.toString(), estimate.max.toString()],
      };

      // Include estimate details (breakdown, notes, confidence) if available
      if (estimate.breakdown) {
        bookingPayload.estimateDetails = {
          breakdown: estimate.breakdown,
          notes: estimate.notes,
          confidence: estimate.confidence,
        };
      }

      // Handle address based on whether using saved address or new address
      if (savedAddressId) {
        // Using a saved address - just send the ID
        bookingPayload.savedAddressId = savedAddressId;
        logger.debug("Using saved address", { savedAddressId });
      } else {
        // Using a new address - send address data
        bookingPayload.serviceAddress = {
          addressLine1: formData.address.addressLine1,
          addressLine2: formData.address.addressLine2 || "",
          locality: formData.address.locality || formData.address.city,
          region: formData.address.region || formData.address.province,
          postcode: formData.address.postcode || formData.address.postalCode,
          country: formData.address.country,
        };
      }

      // If saveAddress is true and not using saved address (unauth flow becoming auth), tell backend to save
      if (saveAddress && !savedAddressId) {
        bookingPayload.saveAddress = true;
        logger.debug("Will save new address on submission");
      }

      logger.debug("Creating booking", { bookingPayload });

      // Make API call
      const data = await bookingService.submitBooking(bookingPayload);

      logger.debug("Booking created successfully", { booking: data });

      // Kick off background photo upload (non-blocking)
      startUpload(data.id);

      return data; // Return the created booking
    } catch (error) {
      logger.error("Error confirming booking", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to create booking",
      );
    }
  },
);

// ==================== ACTION CREATORS ====================

/**
 * Action to go to a specific step (for Edit links)
 */
export const goToStep = (stepId) => (dispatch, _getState) => {
  dispatch(setCurrentStep(stepId));
  logger.debug("Navigated to step", { stepId });
};

// ==================== SLICE ====================

const initialState = {
  // Service info
  serviceSlug: null,
  serviceName: null,
  categoryId: null,

  // Navigation state
  currentStep: "service-details", // 'service-details' | 'address' | 'datetime' | 'estimate' | 'payment' | 'review'
  currentQuestionIndex: 0, // For service-details step

  // Form data
  formData: {
    serviceDetails: {}, // Question answers
    datetime: null, // { date, time }
    address: null, // { addressLine1, addressLine2, city, province, postalCode, country } - raw address data
  },

  // Address selection (for authenticated users)
  savedAddressId: null, // ID of selected saved address (if using saved)
  saveAddress: false, // Flag to save new address on submission (for unauth flow)

  // Estimate
  estimate: null, // { min, max, confidence, breakdown, notes, isFallback? }
  estimateLoading: false,
  estimateError: null,
  estimateInvalidated: false, // Flag to track if estimate needs refresh

  // Booking submission
  submittedBooking: null,
  submitting: false,
  submitError: null,

  // Photos (File count — actual File objects live in photoStore)
  pendingPhotoCount: 0,

  // Progress management
  hasSavedProgress: false,
  progressLoaded: false,
};

const bookingFlowSlice = createSlice({
  name: "bookingFlow",
  initialState,
  reducers: {
    /**
     * Initialize booking flow for a service
     */
    initializeBooking: (state, action) => {
      const { serviceSlug, serviceName, categoryId } = action.payload;

      // Reset state
      Object.assign(state, initialState);

      // Set service info
      state.serviceSlug = serviceSlug;
      state.serviceName = serviceName;
      state.categoryId = categoryId;

      logger.debug("Booking flow initialized", { serviceSlug, serviceName });
    },

    /**
     * Load saved progress from localStorage
     */
    loadSavedProgress: (state, action) => {
      const { serviceSlug } = action.payload;
      const progress = loadProgress(serviceSlug);

      if (progress) {
        state.currentStep = progress.currentStep;
        state.currentQuestionIndex = progress.currentQuestionIndex;
        state.formData = progress.formData;
        state.estimate = progress.estimate;
        state.savedAddressId = progress.savedAddressId || null;
        state.saveAddress = progress.saveAddress || false;
        state.hasSavedProgress = true;

        logger.info("Loaded saved booking progress", {
          step: progress.currentStep,
          savedAt: progress.savedAt,
        });
      }

      state.progressLoaded = true;
    },

    /**
     * Clear saved progress and start fresh
     */
    clearSavedProgress: (state) => {
      clearProgress();
      state.hasSavedProgress = false;
      state.currentStep = "service-details";
      state.currentQuestionIndex = 0;
      state.formData = {
        serviceDetails: {},
        datetime: null,
        address: null,
      };
      state.savedAddressId = null;
      state.saveAddress = false;
      state.estimate = null;

      logger.info("Cleared saved booking progress");
    },

    /**
     * Answer a question in service-details step
     */
    answerQuestion: (state, action) => {
      const { questionId, value } = action.payload;
      const prev = state.formData.serviceDetails[questionId];
      state.formData.serviceDetails[questionId] = value;

      // Invalidate estimate only when the answer actually changes
      if (JSON.stringify(prev) !== JSON.stringify(value)) {
        state.estimateInvalidated = true;
      }

      // Save progress
      saveProgress(state);

      logger.debug("Question answered", { questionId, value });
    },

    /**
     * Go to next question in service-details
     */
    nextQuestion: (state, action) => {
      const form = getBookingForm(state.serviceSlug);
      if (!form) return;

      const serviceDetailsStep = form.steps.find(
        (s) => s.id === "service-details",
      );
      if (!serviceDetailsStep) return;

      const visibleQuestions = getVisibleQuestions(
        serviceDetailsStep.questions,
        state.formData.serviceDetails,
      );

      if (state.currentQuestionIndex < visibleQuestions.length - 1) {
        state.currentQuestionIndex++;
        logger.debug("Moved to next question", {
          index: state.currentQuestionIndex,
        });
      }
    },

    /**
     * Go to previous question in service-details
     */
    previousQuestion: (state) => {
      if (state.currentQuestionIndex > 0) {
        state.currentQuestionIndex--;
        logger.debug("Moved to previous question", {
          index: state.currentQuestionIndex,
        });
      }
    },

    /**
     * Complete service-details step and move to next step
     */
    completeServiceDetails: (state) => {
      state.currentStep = "address";
      state.currentQuestionIndex = 0;

      // Save progress
      saveProgress(state);

      logger.debug("Service details completed, moving to address");
    },

    /**
     * Set date and time
     */
    setDateTime: (state, action) => {
      const prevDateTime = state.formData.datetime?.dateTime;
      state.formData.datetime = action.payload;
      state.currentStep = "estimate";

      if (prevDateTime !== action.payload?.dateTime) {
        state.estimateInvalidated = true;
      }

      // Save progress
      saveProgress(state);

      logger.debug("Date/time set", action.payload);
    },

    /**
     * Set service address (new address entry - used for unauth users or new address)
     * This sets the raw address data and marks saveAddress = true for unauth flow
     */
    setAddress: (state, action) => {
      const { address, saveAddress = false } = action.payload;
      const prevRegion =
        state.formData.address?.region || state.formData.address?.province;
      const newRegion = address?.region || address?.province;
      state.formData.address = address;
      state.savedAddressId = null; // Clear saved address ID when entering new address
      state.saveAddress = saveAddress;
      state.currentStep = "datetime";

      if (prevRegion !== newRegion) {
        state.estimateInvalidated = true;
      }

      // Save progress
      saveProgress(state);

      logger.debug("Address set", { address, saveAddress });
    },

    /**
     * Select a saved address (for authenticated users)
     */
    selectSavedAddress: (state, action) => {
      const { addressId, addressData } = action.payload;
      const prevRegion =
        state.formData.address?.region || state.formData.address?.province;
      const newRegion = addressData?.region || addressData?.province;
      state.savedAddressId = addressId;
      state.formData.address = addressData; // Store for display purposes
      state.saveAddress = false; // Don't save, it's already saved
      state.currentStep = "datetime";

      if (prevRegion !== newRegion) {
        state.estimateInvalidated = true;
      }

      // Save progress
      saveProgress(state);

      logger.debug("Saved address selected", { addressId });
    },

    /**
     * Initialize a saved address from loaded progress (without navigating)
     * Used during address step initialization to ensure state consistency
     *
     * Similar to selectSavedAddress, but without navigating to next step
     */
    initializeSavedAddress: (state, action) => {
      const { addressId, addressData } = action.payload;
      state.savedAddressId = addressId;
      state.formData.address = addressData; // Store for display purposes
      state.saveAddress = false; // Don't save, it's already saved
      // Do NOT change currentStep - just synchronize the address state

      // Save progress
      saveProgress(state);

      logger.debug("Saved address initialized", { addressId });
    },

    /**
     * Go back to previous step
     */
    goToPreviousStep: (state) => {
      const stepOrder = [
        "service-details",
        "address",
        "datetime",
        "estimate",
        "payment",
        "review",
      ];
      const currentIndex = stepOrder.indexOf(state.currentStep);

      if (currentIndex > 0) {
        state.currentStep = stepOrder[currentIndex - 1];

        // If going back to service-details, set question index to last question
        if (state.currentStep === "service-details") {
          const form = getBookingForm(state.serviceSlug);
          if (form) {
            const serviceDetailsStep = form.steps.find(
              (s) => s.id === "service-details",
            );
            if (serviceDetailsStep) {
              const visibleQuestions = getVisibleQuestions(
                serviceDetailsStep.questions,
                state.formData.serviceDetails,
              );
              state.currentQuestionIndex = Math.max(
                0,
                visibleQuestions.length - 1,
              );
            }
          }
        }

        logger.debug("Moved to previous step", { step: state.currentStep });
      }
    },

    /**
     * Go to payment step (after auth gate)
     */
    goToPaymentStep: (state) => {
      state.currentStep = "payment";
      saveProgress(state);
      logger.debug("Moved to payment step");
    },

    /**
     * Go to review step (after payment)
     */
    goToReviewStep: (state) => {
      state.currentStep = "review";
      saveProgress(state);
      logger.debug("Moved to review step");
    },

    /**
     * Set current step explicitly (to enable Edit links)
     */
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
      saveProgress(state);

      logger.debug("Current step changed", { step: action.payload });
    },

    /**
     * Clear estimate error
     */
    clearEstimateError: (state) => {
      state.estimateError = null;
    },

    /**
     * Clear submit error
     */
    clearSubmitError: (state) => {
      state.submitError = null;
    },

    /**
     * Track how many photos the user has selected (for display only)
     */
    setPendingPhotoCount: (state, action) => {
      state.pendingPhotoCount = action.payload;
    },

    /**
     * Reset booking flow completely
     */
    resetBookingFlow: (state) => {
      Object.assign(state, initialState);
      clearProgress();
      clearPendingPhotos();
      resetUploadState();
      logger.info("Booking flow reset");
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch estimate
      .addCase(fetchPriceEstimate.pending, (state) => {
        state.estimateLoading = true;
        state.estimateError = null;
      })
      .addCase(fetchPriceEstimate.fulfilled, (state, action) => {
        state.estimateLoading = false;
        state.estimate = action.payload;
        state.estimateInvalidated = false; // Mark as valid

        // Save progress with estimate
        saveProgress(state);
      })
      .addCase(fetchPriceEstimate.rejected, (state, action) => {
        state.estimateLoading = false;
        state.estimateError = action.payload;
      });

    // Submit booking
    builder
      .addCase(submitBooking.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        logger.debug("Submitting booking - pending");
      })
      .addCase(submitBooking.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitError = null;
        state.submittedBooking = action.payload;
        state.currentStep = "confirmation";
        state.pendingPhotoCount = 0;
        clearProgressFromStorage();
        logger.debug("Booking submitted successfully", {
          booking: action.payload,
        });
      })
      .addCase(submitBooking.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
        logger.error("Booking submission failed", { error: action.payload });
      });
  },
});

// ==================== SELECTORS ====================

const selectBookingFlow = (state) => state.bookingFlow;

export const selectServiceInfo = createSelector(
  [selectBookingFlow],
  (bookingFlow) => ({
    slug: bookingFlow.serviceSlug,
    name: bookingFlow.serviceName,
    categoryId: bookingFlow.categoryId,
  }),
);

export const selectCurrentStep = (state) => state.bookingFlow.currentStep;
export const selectCurrentQuestionIndex = (state) =>
  state.bookingFlow.currentQuestionIndex;
export const selectFormData = (state) => state.bookingFlow.formData;
export const selectServiceDetails = (state) =>
  state.bookingFlow.formData.serviceDetails;
export const selectEstimate = (state) => state.bookingFlow.estimate;
export const selectEstimateLoading = (state) =>
  state.bookingFlow.estimateLoading;
export const selectEstimateError = (state) => state.bookingFlow.estimateError;
export const selectEstimateInvalidated = (state) =>
  state.bookingFlow.estimateInvalidated;
export const selectSubmitting = (state) => state.bookingFlow.submitting;
export const selectSubmitError = (state) => state.bookingFlow.submitError;
export const selectSubmittedBooking = (state) =>
  state.bookingFlow.submittedBooking;
export const selectHasSavedProgress = (state) =>
  state.bookingFlow.hasSavedProgress;
export const selectProgressLoaded = (state) => state.bookingFlow.progressLoaded;
export const selectPendingPhotoCount = (state) =>
  state.bookingFlow.pendingPhotoCount;

// Get current visible question
export const selectCurrentQuestion = (state) => {
  const form = getBookingForm(state.bookingFlow.serviceSlug);
  if (!form) return null;

  const serviceDetailsStep = form.steps.find((s) => s.id === "service-details");
  if (!serviceDetailsStep) return null;

  const visibleQuestions = getVisibleQuestions(
    serviceDetailsStep.questions,
    state.bookingFlow.formData.serviceDetails,
  );

  return visibleQuestions[state.bookingFlow.currentQuestionIndex] || null;
};

// Get total visible questions count
export const selectVisibleQuestionsCount = (state) => {
  const form = getBookingForm(state.bookingFlow.serviceSlug);
  if (!form) return 0;

  const serviceDetailsStep = form.steps.find((s) => s.id === "service-details");
  if (!serviceDetailsStep) return 0;

  const visibleQuestions = getVisibleQuestions(
    serviceDetailsStep.questions,
    state.bookingFlow.formData.serviceDetails,
  );

  return visibleQuestions.length;
};

// Check if current step is complete
export const selectIsStepComplete = (state) => {
  const step = state.bookingFlow.currentStep;
  const formData = state.bookingFlow.formData;

  switch (step) {
    case "service-details":
      const form = getBookingForm(state.bookingFlow.serviceSlug);
      if (!form) return false;

      const serviceDetailsStep = form.steps.find(
        (s) => s.id === "service-details",
      );
      if (!serviceDetailsStep) return false;

      const visibleQuestions = getVisibleQuestions(
        serviceDetailsStep.questions,
        formData.serviceDetails,
      );

      // All required visible questions must be answered
      return visibleQuestions
        .filter((q) => q.required)
        .every((q) => formData.serviceDetails[q.id]);

    case "datetime":
      return !!formData.datetime;

    case "address":
      return !!formData.address || !!state.bookingFlow.savedAddressId;

    case "estimate":
      return !!state.bookingFlow.estimate;

    default:
      return false;
  }
};

// Address-related selectors
export const selectSavedAddressId = (state) => state.bookingFlow.savedAddressId;
export const selectSaveAddress = (state) => state.bookingFlow.saveAddress;

// ==================== EXPORTS ====================

export const {
  initializeBooking,
  loadSavedProgress,
  clearSavedProgress,
  answerQuestion,
  nextQuestion,
  previousQuestion,
  completeServiceDetails,
  setDateTime,
  setAddress,
  selectSavedAddress,
  initializeSavedAddress,
  goToPreviousStep,
  goToPaymentStep,
  goToReviewStep,
  clearEstimateError,
  clearSubmitError,
  setPendingPhotoCount,
  resetBookingFlow,
  setCurrentStep,
} = bookingFlowSlice.actions;

export default bookingFlowSlice.reducer;
